import React, { useState, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, Typography, Button, Stack, GlobalStyles,
  Accordion, AccordionSummary, AccordionDetails,
  ToggleButton, ToggleButtonGroup, Dialog, Divider, DialogActions, Paper, Grid
} from '@mui/material';
import {
  DirectionsCar as CarIcon, ExpandMore as ExpandMoreIcon,
  EventNote as DateIcon, Business as BusinessIcon,
  PlayCircleFilled as OngoingIcon, KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import { format, getDaysInMonth, setYear, setMonth, setDate, setHours, setMinutes } from 'date-fns';
import { ko } from 'date-fns/locale';
import SlotWheel from '../common/util/SlotWheel'; 

const CarListForm = ({
  tabValue, setTabValue, useRows, reservRows, reservUserRows, ongoingRows, 
  completedRows, statusCounts, hasOngoing, formatWithDay, 
  handleStartStop, handleLogout, setDates, dates
}) => {
  // 더보기 상태 관리 (각 탭별로 3개씩 초기화)
  const [visibleReserv, setVisibleReserv] = useState(3);
  const [visibleCompleted, setVisibleCompleted] = useState(3);

  const [isCorporate, setIsCorporate] = useState(true); // 기본값을 법인(true)으로 설정
  const [pickerTarget, setPickerTarget] = useState(null); // 'start' | 'end' | null
  const [showAll, setShowAll] = useState(false); // 리스트 펼침 상태 관리

  // 현재 활성화된 타겟('start' 또는 'end')의 날짜 객체
  const currentVal = pickerTarget ? dates[pickerTarget] : new Date();

    // 피커 열기 함수
  const openPicker = (target) => setPickerTarget(target);

  // 2. Picker 닫기 함수
  const closePicker = () => setPickerTarget(null);

  // 3. 날짜 업데이트 핸들러
  const updateDate = (type, value) => {
    if (!pickerTarget) return;

    let newDate = new Date(currentVal);
    
    switch (type) {
      case 'year': newDate = setYear(newDate, value); break;
      case 'month': newDate = setMonth(newDate, value - 1); break;
      case 'day': newDate = setDate(newDate, value); break;
      case 'hour': newDate = setHours(newDate, value); break;
      case 'minute': newDate = setMinutes(newDate, value); break;
      default: break;
    }

    setDates(prev => ({ ...prev, [pickerTarget]: newDate }));
  };

  // 4. 슬롯에 표시할 데이터 배열 생성 (Memoization)
  const years = useMemo(() => Array.from({ length: 11 }, (_, i) => 2020 + i), []);
  const months = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);
  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);
  const minutes = useMemo(() => Array.from({ length: 60 }, (_, i) => i), []);
  
  // 선택된 연/월에 따른 일수 계산 (예: 2월은 28일 또는 29일)
  const daysInMonth = useMemo(() => {
    const days = getDaysInMonth(currentVal);
    return Array.from({ length: days }, (_, i) => i + 1);
  }, [currentVal]);

const handleTypeChange = (event, newAlignment) => {
  if (newAlignment !== null) {
    setIsCorporate(newAlignment === "corporate");
  }
};

  return (
    <>
      <GlobalStyles styles={{ 
        body: { backgroundColor: '#F8F9FA !important', margin: 0 },
        html: { backgroundColor: '#F8F9FA !important' } 
      }} />
      <Box sx={{ p: 1, bgcolor: '#F8F9FA', minHeight: '100dvh' }}>
        {/* 헤더 영역 */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CarIcon color="primary" sx={{ fontSize: 24 }} /> 차량 이용 현황
          </Typography>
          <Stack 
            direction="row" 
            spacing={1} 
            alignItems="center" 
            sx={{ mb: 1, mt: 1 }} 
          >
            <Box sx={{ flexGrow: 1 }} />
            <Button 
              onClick={handleStartStop}
              variant="contained" 
              color={hasOngoing ? "secondary" : "primary"} 
              size="small" 
              sx={{ borderRadius: 5, fontWeight: 700 }}
            >
              {hasOngoing ? "운행 후 기록" : "운행 전 기록"}
            </Button>
            
            <Button 
              variant="outlined" 
              color="error" 
              size="small" 
              onClick={handleLogout} 
              sx={{ borderRadius: 5, fontWeight: 700 }}
            >
              로그아웃
            </Button>
          </Stack>
        </Stack>
        <Button 
          component={RouterLink} 
          to="/carusage/reservation" 
          state={{ type: 'create', reservData: reservUserRows }}
          variant="contained" fullWidth size="large" sx={{ mb: 1, py: 1, fontWeight: 800, backgroundColor: '#1976d2' }}
        >
          예약하기
        </Button>

        <Stack spacing={1}>
          {/* 실시간 차량 현황 아코디언 */}
          <Accordion disableGutters elevation={0} sx={{ border: '1px solid #E0E0E0', borderRadius: '8px !important', overflow: 'hidden' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ bgcolor: '#fff', minHeight: '48px !important' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>실시간 차량 현황</Typography>
                <Stack direction="row" spacing={1}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#1702d1' }}>운행 중: </Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#1702d1' }}>{statusCounts.driving}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#03910a' }}>예약 가능: </Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#03910a' }}>{statusCounts.available}</Typography>
                  </Stack>
                </Stack>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0, borderTop: '1px solid #E0E0E0' }}>
              {useRows.map((item, index) => (
                <Stack key={index} direction="row" alignItems="center" sx={{ px: 2, py: 1.2, borderBottom: index === useRows.length - 1 ? 'none' : '1px solid #F0F0F0' }}>
                  <Typography variant="body2" sx={{ fontWeight: 800, width: '85px', color: '#000' }}>{item.carNumber}</Typography>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flex: 1, px: 2 }}>
                    <Typography variant="body2" sx={{ color: '#000' }}>{item.carModel}</Typography>
                    {item.statusYn === 'S' && item.usrNm && <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#000', mr: 1 }}>{item.usrNm}</Typography>}
                  </Stack>
                  <Box sx={{ width: '68px', textAlign: 'center', py: 0.3, borderRadius: 1, fontSize: '0.7rem', fontWeight: 800, bgcolor: item.statusYn === 'S' ? '#E1F5FE' : '#F0F7FF', color: item.statusYn === 'S' ? '#0288D1' : '#007FFF' }}>
                    {item.statusYn === 'S' ? '운행 중' : '예약가능'}
                  </Box>
                </Stack>
              ))}
            </AccordionDetails>
          </Accordion>

          {/* 진행 중인 운행 카드 */}
          {ongoingRows.map((item, idx) => (
            <Card key={`ongoing-${idx}`} sx={{ borderRadius: 2, border: '2px solid #0288D1', bgcolor: '#F0F9FF', boxShadow: 'none' }}>
              <Box sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 900, color: '#000', mt: 0.5 }}>{item.carNumber} ({item.carModel})</Typography>
                  <Box sx={{ px: 1.2, borderRadius: 2, fontSize: '0.7rem', fontWeight: 800, bgcolor: '#E1F5FE', color: '#0288D1' }}>사용 중</Box>
                </Stack>
                <Stack spacing={1.2}>
                  <Stack direction="row" spacing={1}><OngoingIcon sx={{ fontSize: 16, color: '#0288D1', mt: 0.2 }} /><Box><Typography variant="caption" sx={{ color: '#01579B', fontWeight: 800, display: 'block', fontSize: '0.65rem' }}>운행시작</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#444', mt: -0.2 }}>{formatWithDay(item.startDate)}</Typography></Box></Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
                    <Stack direction="row" spacing={1}><BusinessIcon sx={{ fontSize: 16, color: '#666', mt: 0.2 }} /><Box><Typography variant="caption" sx={{ color: '#666', fontWeight: 800, display: 'block', fontSize: '0.65rem' }}>방문지</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#444', mt: -0.2 }}>{item.visitPartnerNm || '-'}</Typography></Box></Stack>
                    <Button variant="contained" size="small" onClick={handleStartStop} sx={{ minWidth: '80px', height: '32px', bgcolor: '#7e02d1', fontWeight: 900, fontSize: '0.75rem', borderRadius: 1.5 }}>운행 후 기록</Button>
                  </Stack>
                </Stack>
              </Box>
            </Card>
          ))}

          {/* 탭 영역 (예약 내역 / 이용 기록) */}
          <Box sx={{ borderRadius: 3, border: '1px solid #E0E0E0', overflow: 'hidden', bgcolor: '#fff' }}>
            <Stack direction="row" sx={{ bgcolor: '#F1F3F5', p: 0.7 }}>
              <Box onClick={() => setTabValue(0)} sx={{ flex: 1, py: 1.2, textAlign: 'center', cursor: 'pointer', borderRadius: 2, bgcolor: tabValue === 0 ? '#fff' : 'transparent', color: tabValue === 0 ? '#1976d2' : '#666', transition: '0.2s' }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>예약 조회</Typography>
              </Box>
              <Box onClick={() => setTabValue(1)} sx={{ flex: 1, py: 1.2, textAlign: 'center', cursor: 'pointer', borderRadius: 2, bgcolor: tabValue === 1 ? '#fff' : 'transparent', color: tabValue === 1 ? '#1976d2' : '#666', transition: '0.2s' }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>예약 내역</Typography>
              </Box>
              <Box onClick={() => setTabValue(2)} sx={{ flex: 1, py: 1.2, textAlign: 'center', cursor: 'pointer', borderRadius: 2, bgcolor: tabValue === 2 ? '#fff' : 'transparent', color: tabValue === 2 ? '#1976d2' : '#666', transition: '0.2s' }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>이용 기록</Typography>
              </Box>
            </Stack>

            <Box sx={{ p: 0 }}>
              {/* 예약 내역 탭 */}
              {tabValue === 0 && (
                <>
                  {/* 1. 조회 기간 설정 영역 */}
                  <Box 
                    onClick={() => openPicker('start')}
                    sx={{ 
                      position: 'relative', p: '14px 10px', 
                      border: '1px solid #ccc', borderRadius: 1.5, bgcolor: '#fff', 
                      cursor: 'pointer', mt: 1.2
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        position: 'absolute', top: -10, left: 10, px: 0.5, 
                        bgcolor: '#fff', color: 'text.secondary', 
                        fontWeight: 800, fontSize: '0.75rem', zIndex: 1 
                      }}
                    >
                      조회 기간
                    </Typography>

                    <Grid container alignItems="center">
                      <Grid item xs={5.5} sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.88rem', fontWeight: 700 }}>
                          {format(dates.start, 'yyyy-MM-dd')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: 'primary.main', fontWeight: 800 }}>
                          {format(dates.start, '(eee) HH:mm', { locale: ko })}
                        </Typography>
                      </Grid>
                      <Grid item xs={1} sx={{ textAlign: 'center' }}>
                        <Typography sx={{ color: 'text.disabled', fontWeight: 900 }}>~</Typography>
                      </Grid>
                      <Grid 
                        item xs={5.5} sx={{ textAlign: 'center' }} 
                        onClick={(e) => { e.stopPropagation(); openPicker('end'); }}
                      >
                        <Typography sx={{ fontSize: '0.88rem', fontWeight: 700 }}>
                          {format(dates.end, 'yyyy-MM-dd')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: 'primary.main', fontWeight: 800 }}>
                          {format(dates.end, '(eee) HH:mm', { locale: ko })}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box 
                    sx={{ 
                      position: 'relative', 
                      mt: 2.5, 
                      p: '20px 10px 10px 10px', 
                      border: '1px solid #ccc', 
                      borderRadius: 1.5, 
                      bgcolor: '#fff' 
                    }}
                  >
                    {/* 상단 겹치는 라벨 */}
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        position: 'absolute', top: -10, left: 10, px: 0.5, 
                        bgcolor: '#fff', color: 'text.secondary', 
                        fontWeight: 700, fontSize: '0.75rem', zIndex: 1 
                      }}
                    >
                      예약 현황
                    </Typography>

                    <Stack spacing={1.2}>
                      {reservRows.length > 0 ? (
                        <>
                          {/* 리스트 출력: visibleReserv 개수만큼 노출 */}
                          {reservRows.slice(0, visibleReserv).map((res, idx) => {
                            const statusTheme = 
                              res.statusYn === 'S' ? { border: '#0288D1', bg: '#f0faff' } : 
                              res.statusYn === 'E' ? { border: '#7B1FA2', bg: '#fcfaff' } : 
                              res.statusYn === 'N' ? { border: '#bdbdbd', bg: '#fafafa' } : 
                              { border: '#2E7D32', bg: '#f9fff9' };

                            return (
                              <Card
                                key={idx}
                                variant="outlined"
                                sx={{
                                  borderRadius: 2,
                                  borderStyle: res.statusYn === 'N' ? 'dashed' : 'solid',
                                  borderWidth: 1.2,
                                  borderColor: statusTheme.border,
                                  bgcolor: statusTheme.bg,
                                }}
                              >
                                <Box sx={{ p: 1.2 }}>
                                  <Stack spacing={0.5} sx={{ mb: 0.8 }}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: '#333' }}>
                                        {res.carNumber} ({res.carModel})
                                      </Typography>
                                      <Box sx={{ 
                                        px: 1, py: 0.2, borderRadius: 1, fontSize: '0.65rem', fontWeight: 900, 
                                        bgcolor: statusTheme.border, color: '#fff' 
                                      }}>
                                        {res.statusYn === 'S' ? '운행 중' : res.statusYn === 'E' ? '반납 완료' : res.statusYn === 'N' ? '예약 취소' : '예약'}
                                      </Box>
                                    </Stack>
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#555' }}>
                                      예약자: {res.usrNm} ({res.dptNm})
                                    </Typography>
                                  </Stack>
                                  
                                  <Divider sx={{ my: 0.8, opacity: 0.3 }} />

                                  <Box>
                                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'text.secondary' }}>
                                      예약기간
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#333', lineHeight: 1.4 }}>
                                      {res.startDate} ~ {res.endDate || '진행중'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Card>
                            );
                          })}

                          {/* 더보기 버튼 영역: 데이터가 더 있을 때만 노출 */}
                          {reservRows.length > visibleReserv && (
                            <Box sx={{ p: 1.5, textAlign: 'center', borderTop: '1px solid #F1F3F5' }}>
                              <Button 
                                fullWidth 
                                endIcon={<ArrowDownIcon />} 
                                onClick={() => setVisibleReserv(prev => prev + 3)}
                                sx={{ color: '#888', fontWeight: 700, fontSize: '0.8rem' }}
                              >
                                더보기 ({Math.min(visibleReserv, reservRows.length)}/{reservRows.length})
                              </Button>
                            </Box>
                          )}
                        </>
                      ) : (
                        <Box sx={{ py: 4, textAlign: 'center', bgcolor: '#f9f9f9', borderRadius: 2, border: '1px dashed #e0e0e0' }}>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.disabled' }}>
                            예약 현황이 없습니다.
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </>
              )}
              {tabValue === 1 && (
                <Box>
                  {reservUserRows.length > 0 ? (
                    <>
                      {reservUserRows.slice(0, visibleReserv).map((res, idx) => (
                        <Box key={idx} sx={{ p: 2, borderBottom: idx === reservUserRows.slice(0, visibleReserv).length - 1 && reservUserRows.length <= visibleReserv ? 'none' : '1px solid #F1F3F5' }}>
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 900, color: '#000', mt: 0.5 }}>{res.carNumber} ({res.carModel})</Typography>
                            <Box sx={{ 
                              px: 1.2, py: 0.5, borderRadius: 2, fontSize: '0.7rem', fontWeight: 800,
                              bgcolor: res.statusYn === 'S' ? '#E1F5FE' : res.statusYn === 'E' ? '#F3E5F5' : res.statusYn === 'N' ? '#F5F5F5' : '#E8F5E9',
                              color: res.statusYn === 'S' ? '#0288D1' : res.statusYn === 'E' ? '#7B1FA2' : res.statusYn === 'N' ? '#9E9E9E' : '#2E7D32'
                            }}>{res.statusYn === 'S' ? '운행 중' : res.statusYn === 'E' ? '반납 완료' : res.statusYn === 'N' ? '예약취소' : '예약완료'}</Box>
                          </Stack>
                          <Stack spacing={1}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <DateIcon sx={{ fontSize: 16, color: '#0288D1' }} />
                              <Typography sx={{ color: '#01579B', fontWeight: 800, fontSize: '0.7rem', minWidth: '50px' }}>대여시작</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#444' }}>{formatWithDay(res.startDate)}</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <DateIcon sx={{ fontSize: 16, color: res.statusYn === 'E' ? '#7B1FA2' : '#D84315' }} />
                              <Typography 
                                sx={{ 
                                  color: res.statusYn === 'E' ? '#7B1FA2' : '#D84315', 
                                  fontWeight: 800, 
                                  fontSize: '0.7rem', 
                                  minWidth: '50px' 
                                }}
                              >
                                {res.statusYn === 'E' ? '반납완료' : '반납예정'}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {formatWithDay(res.endDate)}
                              </Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flex: 1 }}>
                                <BusinessIcon sx={{ fontSize: 16, color: '#666' }} />
                                <Typography sx={{ color: '#666', fontWeight: 800, fontSize: '0.7rem', minWidth: '50px' }}>방문지</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{res.visitPartnerNm || '-'}</Typography>
                              </Stack>
                              <Button variant="contained" size="small" component={RouterLink} to="/carusage/reservation" state={{ type: 'edit', data: res }} sx={{ minWidth: '50px', height: '24px', bgcolor: '#1976d2', fontWeight: 900, fontSize: '0.75rem', borderRadius: 1.5 }}>상세보기</Button>
                            </Stack>
                          </Stack>
                        </Box>
                      ))}
                      {reservUserRows.length > visibleReserv && (
                        <Box sx={{ p: 1.5, textAlign: 'center', borderTop: '1px solid #F1F3F5' }}>
                          <Button 
                            fullWidth 
                            endIcon={<ArrowDownIcon />} 
                            onClick={() => setVisibleReserv(prev => prev + 3)}
                            sx={{ color: '#888', fontWeight: 700, fontSize: '0.8rem' }}
                          >
                            더보기 ({visibleReserv}/{reservUserRows.length})
                          </Button>
                        </Box>
                      )}
                    </>
                  ) : <Typography sx={{ textAlign: 'center', py: 8, color: '#ADB5BD', fontWeight: 700 }}>예약 내역이 없습니다.</Typography>}
                </Box>
              )}

              {/* 이용 기록 탭 */}
              {tabValue === 2 && (
                <Box>
                  {completedRows.length > 0 ? (
                    <>
                      {completedRows.slice(0, visibleCompleted).map((item, idx) => (
                        <Box key={idx} sx={{ p: 2, borderBottom: idx === completedRows.slice(0, visibleCompleted).length - 1 && completedRows.length <= visibleCompleted ? 'none' : '1px solid #F1F3F5' }}>
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 900, color: '#000', mt: 0.5 }}>{item.carNumber} ({item.carModel})</Typography>
                            <Box sx={{ px: 1.2, py: 0.5, borderRadius: 2, fontSize: '0.7rem', fontWeight: 800, bgcolor: '#F0F7FF', color: '#007FFF' }}>사용 완료</Box>
                          </Stack>
                          <Stack spacing={1}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <DateIcon sx={{ fontSize: 16, color: '#888' }} />
                              <Typography sx={{ color: '#666', fontWeight: 800, fontSize: '0.7rem', minWidth: '50px' }}>운행시작</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#444' }}>{formatWithDay(item.startDate)}</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <DateIcon sx={{ fontSize: 16, color: '#007FFF' }} />
                              <Typography sx={{ color: '#0059B2', fontWeight: 800, fontSize: '0.7rem', minWidth: '50px' }}>운행종료</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#444' }}>{formatWithDay(item.endDate)}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flex: 1 }}>
                                <BusinessIcon sx={{ fontSize: 16, color: '#666' }} />
                                <Typography sx={{ color: '#666', fontWeight: 800, fontSize: '0.7rem', minWidth: '50px' }}>방문지</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.visitPartnerNm || '-'}</Typography>
                              </Stack>
                              <Button variant="contained" size="small" component={RouterLink} to="/carusage/registration" state={{ type: 'edit', data: item }} sx={{ minWidth: '50px', height: '24px', bgcolor: '#1976d2', fontWeight: 900, fontSize: '0.75rem', borderRadius: 1.5 }}>상세보기</Button>
                            </Stack>
                          </Stack>
                        </Box>
                      ))}
                      {completedRows.length > visibleCompleted && (
                        <Box sx={{ p: 1.5, textAlign: 'center', borderTop: '1px solid #F1F3F5' }}>
                          <Button 
                            fullWidth 
                            endIcon={<ArrowDownIcon />} 
                            onClick={() => setVisibleCompleted(prev => prev + 3)}
                            sx={{ color: '#888', fontWeight: 700, fontSize: '0.8rem' }}
                          >
                            더보기 ({visibleCompleted}/{completedRows.length})
                          </Button>
                        </Box>
                      )}
                    </>
                  ) : <Typography sx={{ textAlign: 'center', py: 8, color: '#ADB5BD', fontWeight: 700 }}>이용 기록이 없습니다.</Typography>}
                </Box>
              )}
            </Box>
          </Box>
        </Stack>
      </Box>
       {/* 팝업 피커 (Dialog) */}
      <Dialog 
        open={Boolean(pickerTarget)} 
        onClose={closePicker} 
        fullWidth maxWidth="xs" 
        PaperProps={{ sx: { m: 2, borderRadius: 4, overflow: 'hidden' } }}
      >
        <Stack direction="row" sx={{ bgcolor: '#f8f9fa', p: 1, borderBottom: '1px solid #eee' }}>
          <Button 
            fullWidth 
            variant={pickerTarget === 'start' ? 'contained' : 'text'} 
            onClick={() => setPickerTarget('start')} 
            sx={{ borderRadius: 2, fontWeight: 800 }}
          >시작</Button>
          <Button 
            fullWidth 
            variant={pickerTarget === 'end' ? 'contained' : 'text'} 
            onClick={() => setPickerTarget('end')} 
            sx={{ borderRadius: 2, fontWeight: 800 }}
          >종료</Button>
        </Stack>

        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 900, mb: 3 }}>
            {format(currentVal, 'yyyy년 MM월 dd일 (eee) HH:mm', { locale: ko })}
          </Typography>
          
          <Stack direction="row" spacing={0.5} justifyContent="center">
            {/* SlotWheel은 별도로 정의된 컴포넌트여야 함 */}
            <SlotWheel items={years} currentValue={currentVal.getFullYear()} onSelect={(v) => updateDate('year', v)} unit="년" />
            <SlotWheel items={months} currentValue={currentVal.getMonth() + 1} onSelect={(v) => updateDate('month', v)} unit="월" />
            <SlotWheel items={daysInMonth} currentValue={currentVal.getDate()} onSelect={(v) => updateDate('day', v)} unit="일" />
          </Stack>
          
          <Divider sx={{ my: 2.5 }} />
          
          <Stack direction="row" spacing={1} justifyContent="center">
            <SlotWheel items={hours} currentValue={currentVal.getHours()} onSelect={(v) => updateDate('hour', v)} unit="시" />
            <SlotWheel items={minutes} currentValue={currentVal.getMinutes()} onSelect={(v) => updateDate('minute', v)} unit="분" />
          </Stack>
        </Box>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button 
            onClick={closePicker} // 수정된 핸들러 연결
            variant="contained" 
            fullWidth 
            sx={{ height: 52, borderRadius: 3, fontWeight: 800 }}
          >
            설정 완료
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CarListForm;
